

/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/Stores/ServiceEndpointsStore" />
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { ServiceEndpointsActions, IServiceEndpointItemList } from "Environments/ProviderCreateDetails/Kubernetes/Actions/ServiceEndpointsActions";
import {
    ServiceEndpoint,
    AzureSubscription
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { StoreBase, IStoreState } from "Environments/Flux/Store";

import { IKubernetesCluster } from "Environments/ProviderCreateDetails/Kubernetes/Types";
import { safeParseJson, KubernetesEvents, getResourceGroupFromClusterId } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";

export interface IServiceEndpointsStoreState extends IStoreState {
    endpoints: ServiceEndpoint[];
    subscriptions: AzureSubscription[];
    aadTenantId: string | undefined;
    clusters: IKubernetesCluster[];
    clustersCallbackParameters: { [key: string]: string };
    namespaces: string[];
    namespacesCallbackParameters: { [key: string]: string };
}

export class ServiceEndpointsStore extends StoreBase {

    public static getKey(): string {
        return "service-endpoints-store";
    }

    public initialize(instanceId: string): void {
        super.initialize(instanceId);

        this._actionsHub = ActionsHubManager.GetActionsHub<ServiceEndpointsActions>(ServiceEndpointsActions, instanceId);
        this._actionsHub.getServiceEndpoints.addListener(this._handleUpdateServiceEndpoints);
        this._actionsHub.getAzureSubscriptions.addListener(this._handleGetAzureSubscriptions);
        this._actionsHub.getTenantId.addListener(this._handleGetTenantId);
        this._actionsHub.getKubernetesClusters.addListener(this._handleGetAzureKubeClusters);
        this._actionsHub.getKubernetesClusterNamespaces.addListener(this._handleGetAzureKubeClusterNamespaces);
    }

    protected disposeInternal(): void {
        this._actionsHub.getServiceEndpoints.removeListener(this._handleUpdateServiceEndpoints);
        this._actionsHub.getAzureSubscriptions.removeListener(this._handleGetAzureSubscriptions);
        this._actionsHub.getTenantId.removeListener(this._handleGetTenantId);
        this._actionsHub.getKubernetesClusters.removeListener(this._handleGetAzureKubeClusters);
        this._actionsHub.getKubernetesClusterNamespaces.removeListener(this._handleGetAzureKubeClusterNamespaces);
    }

    private _handleUpdateServiceEndpoints = (endpoints: ServiceEndpoint[]) => {
        this._state.endpoints = endpoints || [];
        this.emitChanged();
    }

    private _handleGetAzureSubscriptions = (subscriptions: AzureSubscription[]) => {
        this._state.subscriptions = subscriptions || [];
        this.emitChanged();
    };

    private _handleGetTenantId = (tenandId: string) => {
        this._state.aadTenantId = tenandId;
        this.emitChanged();
    }

    private _handleGetAzureKubeClusters = (payload: IServiceEndpointItemList) => {
        let clusters: IKubernetesCluster[] = [];
        const clustersJson = payload.data;
        if (clustersJson && clustersJson.length > 0) {
            clustersJson.forEach(clusterJson => {
                const cluster = safeParseJson(clusterJson);
                if (cluster) {
                    let fqdn: string = cluster.properties["fqdn"];
                    if (fqdn && !fqdn.startsWith("https://")) {
                        fqdn = "https://" + fqdn;
                    }

                    clusters.push({
                        id: cluster["id"],
                        name: cluster["name"],
                        fqdn: fqdn,
                        resourceGroup: getResourceGroupFromClusterId(cluster.id as string)
                    });
                }
            });
        }

        if (payload.append) {
            if (!this._state.clusters) {
                this._state.clusters = [];
            }

            this._state.clusters.push(...clusters);
        }
        else {
            this._state.clusters = clusters;
        }

        this._state.clustersCallbackParameters = payload.callbackParameters;
        this.emit(KubernetesEvents.CLUSTERS_FETCHED_EVENT, this);
    }

    private _handleGetAzureKubeClusterNamespaces = (payload: IServiceEndpointItemList) => {
        let k8sNamespaces: string[] = [];
        const namespacesJson = payload.data;
        if (namespacesJson && namespacesJson.length > 0) {
            namespacesJson.forEach(namespaceJson => {
                const k8sNamespace = safeParseJson(namespaceJson);
                if (k8sNamespace) {
                    k8sNamespaces.push(k8sNamespace.metadata["name"]);
                }
            });
        }

        if (payload.append) {
            if (!this._state.namespaces) {
                this._state.namespaces = [];
            }

            this._state.namespaces.push(...k8sNamespaces);
        }
        else {
            this._state.namespaces = k8sNamespaces;
        }

        this._state.namespacesCallbackParameters = payload.callbackParameters;
        this.emit(KubernetesEvents.NAMESPACES_FETCHED_EVENT, this);
    }

    public getState(): IServiceEndpointsStoreState {
        return this._state;
    }

    private _actionsHub: ServiceEndpointsActions;
    private _state: IServiceEndpointsStoreState = {} as IServiceEndpointsStoreState;
}
